import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { InvoiceModel } from '@frontend/api-invoices';
import { PersonQueries, PersonsV3 } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions as ContactActions } from '@frontend/contact-actions';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import {
  calculateAppliedOrEstimatedSurcharge,
  calculatePaidOrUnpaidInvoiceTotalWithSurcharge,
  calculateRemainingBalanceWithoutSurcharge,
  isPaidInvoice,
} from '@frontend/payments-invoice-calculations';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Avatar, Heading, Text } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';

type SummaryCardProps = {
  invoice: InvoiceModel;
};

const styles = {
  summarySectionRow: (isMobile: boolean) => css`
    margin-bottom: ${theme.spacing(3)};
    ${isMobile ? `margin-top: ${theme.spacing(3)};` : ``}
  `,
  summary: css`
    display: flex;
    flex-direction: column;
  `,
  amount: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1, 3)};
    flex-wrap: wrap;
  `,
  heading: (isMobile: boolean) => css`
    font-size: ${theme.fontSize(isMobile ? 22 : 32)};
  `,
  summaryPerson: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  `,
  noMargin: css`
    margin: 0;
  `,
  summaryHeader: (isMobile: boolean) => css`
    font-size: ${theme.fontSize(isMobile ? 16 : 20)};
  `,
  summaryInfo: css`
    font-size: ${theme.font.size.h3};
    font-weight: ${theme.font.weight.bold};
  `,
  contactActions: (isMobile: boolean) => css`
    display: flex;
    ${isMobile &&
    `
      flex: 1;
      justify-content: flex-end;
    `}
  `,
};

export const SummaryCard = ({ invoice }: SummaryCardProps) => {
  const isPaid = isPaidInvoice(invoice);
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  return (
    <section className='summary-card' css={styles.summarySectionRow(isMobile)}>
      <div css={styles.summary}>
        <SummaryCardPerson invoice={invoice} />
        <div css={styles.amount}>
          {isPaid ? <SummaryCardPaidInfo invoice={invoice} /> : <SummaryCardUnpaidInfo invoice={invoice} />}
        </div>
      </div>
    </section>
  );
};
const SummaryCardUnpaidInfo = ({ invoice }: SummaryCardProps) => {
  const { t } = useTranslation('payments');

  const owedText = invoice.surchargingEnabled ? t('Owed ACH/Debit') : t('Owed');

  return (
    <>
      <SummaryInfo title={owedText} info={formatCentsToCurrency(calculateRemainingBalanceWithoutSurcharge(invoice))} />
      {invoice.surchargingEnabled && (
        <>
          <SummaryInfo
            title={t('Owed Credit Card')}
            info={formatCentsToCurrency(calculatePaidOrUnpaidInvoiceTotalWithSurcharge(invoice))}
          />
          <SummaryInfo
            title={t('Surcharge')}
            info={formatCentsToCurrency(calculateAppliedOrEstimatedSurcharge(invoice))}
          />
        </>
      )}
      <SummaryInfo title={t('Payment Due')} info={formatDate(invoice.billedAt, 'MMMM D, YYYY')} />
    </>
  );
};

const SummaryCardPaidInfo = ({ invoice }: SummaryCardProps) => {
  const { t } = useTranslation('payments');

  return (
    <>
      <SummaryInfo title={t('Requested Amount')} info={formatCentsToCurrency(invoice.billedAmount)} />
      {invoice.surchargingEnabled && (
        <SummaryInfo
          title={t('Surcharge Paid')}
          info={formatCentsToCurrency(calculateAppliedOrEstimatedSurcharge(invoice))}
        />
      )}
      <SummaryInfo
        title={t('Total Paid')}
        info={formatCentsToCurrency(calculatePaidOrUnpaidInvoiceTotalWithSurcharge(invoice))}
      />
      <SummaryInfo title={t('Owed')} info={formatCentsToCurrency(calculateRemainingBalanceWithoutSurcharge(invoice))} />
      <SummaryInfo title={t('Payment Due')} info={formatDate(invoice.billedAt, 'MMMM D, YYYY')} />
    </>
  );
};

const SummaryCardPerson = ({ invoice }: SummaryCardProps) => {
  const { t } = useTranslation('payments');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const { data: personData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: invoice.person.id ?? '',
      locationIds: [invoice.locationId],
    },
    {
      enabled: !!invoice.person.id,
    }
  );

  const person = PersonsV3.PersonHelpers.convertPersonV3ToPerson(personData);

  const { data: personImg } = PersonQueries.useGetPersonImage(invoice.person.id, {
    enabled: !!invoice.person.id,
  });

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  return (
    <div css={styles.summaryPerson}>
      <Avatar
        onClick={() => setPersonId(invoice.person.id)}
        size={isMobile ? 'small' : 'large'}
        name={invoice.person.name}
        src={personImg}
      />
      <Heading css={styles.heading(isMobile)}>{invoice.person.name}</Heading>
      <AdaptoActions enableCloseEvent iconColor='inherit' variant='icon-buttons' css={styles.contactActions(isMobile)}>
        <ContactActions.Call label={t('Call')} context={{ person }} />
        <ContactActions.Message
          label={t('Message')}
          context={{ personId: person.PersonID, threadGroupId: invoice.locationId, groupIds: [invoice.locationId] }}
        />
      </AdaptoActions>
    </div>
  );
};

const SummaryInfo = ({ title, info }: { title: string; info: string | undefined | ReactNode }) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  return (
    <div>
      <Text color='subdued' css={[styles.noMargin, styles.summaryHeader(isMobile)]}>
        {title}
      </Text>
      <Text css={[styles.noMargin, styles.summaryInfo]}>{info}</Text>
    </div>
  );
};
